import React from 'react';
import { showNotification, Tab, TabbedShowLayout, withDataProvider } from 'react-admin';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {
  getLanguagesFromConsentTexts,
  getUpdatedCookieConsentTexts,
  handleAPIErrors,
  parseQueryString,
} from '../../../utils';
import EditPageTitle from '../../../components/EditPageTitle';
import TabbedLayoutStyles from '../../../components/TabbedLayoutStyles';
import { WebsitesEditStepWrapper } from './WebsitesEditStepWrapper';
import WebsitesEditPolicy from './WebsitesEditPolicy';
import WebsitesEditPrivacyControls from './WebsitesEditPrivacyControls';
import ConfirmationModal from '../../../components/ConfirmationModal';

class WebsitesEditPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      formData: null,
      chosenLanguage: {},
      cookieConsentTexts: [],
      languages: [],
      website: {},
    };

    ({dataProvider: this.dataProvider} = this.props);
  }

  componentDidMount() {
    this.loadLanguages();
  }

  componentWillReceiveProps(newProps) {
    this.manageLanguageQueryString(newProps);
  }

  async manageLanguageQueryString(nextProps) {
    await new Promise(resolve => setTimeout(resolve, 0));
    const {language: queryLanguageCode} = parseQueryString(nextProps.location.search);
    const {languages, chosenLanguage: {id: currentLanguageCode}} = this.state;
    const {pathname: nextPathname} = nextProps.location;
    const {pathname: currentPathname} = this.props.location;
    if (queryLanguageCode && (currentLanguageCode !== queryLanguageCode)) {
      this.setLanguage(languages, queryLanguageCode);
    } else if (!queryLanguageCode || (currentPathname !== nextPathname)) {
      this.setLanguageQueryString(currentLanguageCode, nextPathname);
    }
  }

  setLanguageQueryString(languageCode, pathName) {
    const allowedPaths = ['policy', 'privacy-controls'];
    const isAllowed = allowedPaths.includes(pathName.split('/').pop());
    if (!languageCode) {
      languageCode = (this.state.languages[0] || {}).id;
    }
    if (isAllowed && languageCode) {
      this.props.history.push({search: `?language=${languageCode}`});
    }
  }

  pickLanguage({id}) {
    this.setLanguageQueryString(id, this.props.location.pathname);
  }

  setLanguage(languages, languageCode = '') {
    const chosenLanguage = languages.filter(({id}) => id === languageCode)[0] || languages[0];
    if (!chosenLanguage) {
      return;
    }
    this.setState({chosenLanguage});
  }

  updateWebsite(payload, websiteId) {
    return this.dataProvider.update('websites', {
      id: websiteId,
      data: payload,
    });
  };

  handleSubmit(formData) {
    this.setState({formData, showDialog: true});
  }

  async save() {
    const {formData} = this.state;
    try {
      let templateHtml = (formData.popUpTemplate || formData.declarationTemplate || formData.choiceBoxTemplate ||
        {html: ''}).html;
      window.Handlebars.compile(templateHtml)({});
    } catch (error) {
      let errorMessage = `Validation error in html code definition: ${error.message}`;
      this.props.showNotification(errorMessage, 'warning');
      return false;
    }

    let websiteId = this.props.id.includes('websites') ? this.props.id : `/websites/${this.props.id}`;

    const {cookieConsentText, ...resource} = formData;
    const payload = Object.assign({
        cookieConsentTexts: getUpdatedCookieConsentTexts(this.state.cookieConsentTexts, cookieConsentText),
      },
      resource,
    );

    return this.updateWebsite(payload, websiteId).then(() => {
      this.props.showNotification('website_save_success');
      this.loadLanguages();
      this.closeDialog();

      return true;
    }).catch(error => {
      this.props.showNotification('website_save_fail',
        'warning',
        {
          messageArgs: {
            message: error.message || '',
          },
        });
      handleAPIErrors(error, this.props.history);
      this.closeDialog();

      return false;
    });
  }

  closeDialog() {
    this.setState({
      showDialog: null,
    });
  }

  async loadLanguages() {
    let websiteId = this.props.id.includes('websites') ? this.props.id : `/websites/${this.props.id}`;
    return this.dataProvider.getOne('websites', {id: websiteId})
      .then(websiteResponse => {
        this.setState({website: websiteResponse.data});
        return this.dataProvider.getMany('cookie_consent_texts', {
          ids: websiteResponse.data.cookieConsentTexts,
        });
      })
      .then(cookieConsentsTextResponse => {
        this.setState({cookieConsentTexts: cookieConsentsTextResponse.data});
        return getLanguagesFromConsentTexts(this.dataProvider, cookieConsentsTextResponse.data);
      })
      .then(languages => {
        this.setState({languages});
        if (!this.state.chosenLanguage.id) {
          this.setLanguageQueryString(null, this.props.location.pathname);
        }
      })
      .catch(error => {
        handleAPIErrors(error, this.props.history);
        console.error(error);
      });
  }

  render() {
    const stepProps = {
      onLanguagePick: lang => this.pickLanguage(lang),
      languages: this.state.languages,
      cookieConsentTextsIds: this.state.website.cookieConsentTexts,
      chosenLanguage: (this.state.chosenLanguage || {}).id,
      id: this.props.id,
      history: this.props.history,
      onSubmit: data => this.handleSubmit(data),
      website: this.state.website,
      websiteEditSetState: this.setState.bind(this),
    };
    const {classes, dataProvider, showNotification, history, ...rest} = this.props;
    return <div className={classes.container}>
      <EditPageTitle onBack={() => history.push('/websites')}
                     title={`Consent Solution ${this.state.website.name || ''}`}/>
      <TabbedShowLayout>
        <Tab label="Policy" data-testid="policy-tab" path="policy">
          <WebsitesEditStepWrapper
            form={WebsitesEditPolicy}
            resourceId={this.state.website.declarationTemplate}
            resourceSource="declaration_templates"
            resourceName="declarationTemplate"
            {...stepProps}
          />
        </Tab>
        <Tab label="Privacy Controls" data-testid="privacy-controls-tab" path="privacy-controls">
          <WebsitesEditStepWrapper
            form={WebsitesEditPrivacyControls}
            resourceId={this.state.website.choiceBoxTemplate}
            resourceSource="choice_box_templates"
            resourceName="choiceBoxTemplate"
            {...stepProps}
          />
        </Tab>
      </TabbedShowLayout>
      <ConfirmationModal
        open={this.state.showDialog}
        title="Are you sure you want to save and publish changes?"
        okText="SAVE"
        cancelText="CANCEL"
        message="The changes will be visible after 10 minutes on all domains associated with this website."
        onCancel={() => this.closeDialog()}
        onOk={() => this.save()}
      />
    </div>;
  }
}

export const WebsitesEdit = withStyles(TabbedLayoutStyles)(
  connect(null, {showNotification})(withDataProvider(WebsitesEditPage)),
);
